import React, { useCallback, useEffect, useRef, useState } from 'react'
import config from '../Config'
import { t } from '../i18n'
import { adEventParams, adTest, scriptBlocked } from '../Utils/ads'
import { track } from '../Utils/analytics'

const FILL_TIMEOUT_MS = 5000
const COLLAPSE_MS = 200

const onScreen = (el) => {
  const r = el.getBoundingClientRect()
  return r.bottom > 0 && r.top < window.innerHeight
}

/* A 300x250 display ad (ads spec 2.4, 4.3): in the feed like a card, or
   sticky in the right column (variant="sidebar"). The slot keeps its height
   while the ad loads, starts loading about one screen before it scrolls into
   view, and removes itself entirely when no ad comes (no fill, 5 s, ad
   blocker), so there's never an empty frame. Always labelled SPONZORISANO.
   A new slot (key) for every feed load: an <ins> is never reused. */
export default function AdSlot({ unit, surface, position, variant = 'feed' }) {
  const ref = useRef(null)
  const [state, setState] = useState('idle') // idle | loading | filled | collapsing | empty
  const params = useCallback(() => adEventParams(surface, position), [surface, position])

  useEffect(() => {
    track('ad_slot_render', params())
  }, [params])

  const gone = useCallback(
    (reason) => {
      track('ad_slot_empty', { ...params(), reason })
      const el = ref.current
      if (!el || !onScreen(el)) {
        setState('empty')
        return
      }
      // Seen: shrink away instead of jumping (spec 4.3).
      el.style.height = `${el.offsetHeight}px`
      void el.offsetHeight // eslint-disable-line no-void
      setState('collapsing')
      setTimeout(() => setState('empty'), COLLAPSE_MS)
    },
    [params]
  )

  // Load when ~1 screen away.
  useEffect(() => {
    if (state !== 'idle' || !ref.current) return undefined
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        io.disconnect()
        setState('loading')
      },
      { rootMargin: '100% 0px' }
    )
    io.observe(ref.current)
    return () => io.disconnect()
  }, [state])

  // The <ins> exists only from 'loading' on: AdSense's push() fills the first
  // unprocessed <ins> on the page, which must be this one.
  useEffect(() => {
    if (state !== 'loading' || !ref.current) return undefined
    const ins = ref.current.querySelector('ins')
    if (!ins.getAttribute('data-adsbygoogle-status')) {
      try {
        ;(window.adsbygoogle = window.adsbygoogle || []).push({})
      } catch (e) {
        gone('blocked')
        return undefined
      }
    }
    let settled = false
    const settle = () => {
      const status = ins?.getAttribute('data-ad-status')
      if (settled || !status) return
      settled = true
      if (status === 'filled') {
        setState('filled')
        track('ad_slot_filled', params())
      } else gone('no_fill') // 'unfilled'
    }
    const mo = new MutationObserver(settle)
    mo.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] })
    settle()
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      gone(scriptBlocked() ? 'blocked' : 'timeout')
    }, FILL_TIMEOUT_MS)
    return () => {
      mo.disconnect()
      clearTimeout(timer)
    }
  }, [state, gone, params])

  // Impression: at least half visible for a second. Click: the window loses
  // focus to the ad's iframe while the pointer is over the slot.
  useEffect(() => {
    if (state !== 'filled' || !ref.current) return undefined
    let timer = null
    let seen = false
    const io = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timer)
        if (entry.intersectionRatio >= 0.5 && !seen) {
          timer = setTimeout(() => {
            seen = true
            track('ad_impression', params())
          }, 1000)
        }
      },
      { threshold: [0, 0.5] }
    )
    io.observe(ref.current)
    let hover = false
    const el = ref.current
    const enter = () => (hover = true)
    const leave = () => (hover = false)
    const blur = () => hover && track('ad_click', params())
    el.addEventListener('mouseenter', enter)
    el.addEventListener('mouseleave', leave)
    window.addEventListener('blur', blur)
    return () => {
      io.disconnect()
      clearTimeout(timer)
      el.removeEventListener('mouseenter', enter)
      el.removeEventListener('mouseleave', leave)
      window.removeEventListener('blur', blur)
    }
  }, [state, params])

  if (state === 'empty') return null
  const classes = ['ad-slot', variant === 'sidebar' ? 'ad-slot-sidebar' : 'feed-item']
  if (state === 'collapsing') classes.push('ad-slot-collapsing')
  return (
    <aside className={classes.join(' ')} ref={ref} aria-label={t('feed.sponsoredLabel')}>
      <div className="ad-label">{t('feed.sponsored')}</div>
      {state === 'idle' ? (
        <div className="ad-unit" />
      ) : (
        <ins
          className="adsbygoogle ad-unit"
          data-ad-client={config.ADS.CLIENT}
          data-ad-slot={unit}
          {...(adTest() ? { 'data-adtest': 'on' } : {})}
        />
      )}
    </aside>
  )
}
